import { createFileRoute } from "@tanstack/react-router";
import { useLocale, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getUserStatusVariant } from "@/lib/labels";
import { formatDate } from "@/lib/format";
import { useAccountDetail } from "@/hooks/use-account-detail";

export const Route = createFileRoute("/_dashboard/profile")({ component: ProfilePage });

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 py-2">
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-6 p-4">
      <div className="flex items-center gap-4">
        <Skeleton className="size-16 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="space-y-1">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ProfilePage() {
  const t = useTranslations("UserDetail");
  const tStatus = useTranslations("UserStatus");
  const tRole = useTranslations("RoleLabels");
  const locale = useLocale();
  const {
    data: detail,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useAccountDetail();

  if (isLoading) {
    return (
      <>
        <PageHeader title={t("tabProfile")} />
        <ProfileSkeleton />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PageHeader title={t("tabProfile")} />
        <div
          role="alert"
          className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center"
        >
          <p className="text-muted-foreground">{t("loadError")}</p>
          <Button
            variant="outline"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? t("retrying") : t("retry")}
          </Button>
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader title={t("tabProfile")} />
        <div className="flex flex-1 items-center justify-center p-4">
          <p className="text-muted-foreground">{t("notAvailable")}</p>
        </div>
      </>
    );
  }

  const { user, firstName, lastName, fiscalNumber, email, roles, avatarUrl } = detail;
  const firstNameStr = firstName ?? "";
  const lastNameStr = lastName ?? "";
  const initials = `${firstNameStr[0] ?? ""}${lastNameStr[0] ?? ""}`;
  const fullName = `${firstNameStr} ${lastNameStr}`.trim();

  const localeLabels: Record<string, string> = {
    it: t("localeIt"),
    en: t("localeEn"),
  };

  return (
    <>
      <PageHeader title={t("tabProfile")} />
      <div className="flex flex-1 flex-col gap-6 p-4">
        {/* Header section */}
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Avatar size="lg">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={fullName} />}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold">
              {fullName || email || t("tabProfile")}
            </h1>
            <div className="flex flex-wrap gap-2">
              <Badge variant={getUserStatusVariant(user.status)}>
                {tStatus(user.status as "active")}
              </Badge>
              {roles.map((role) => (
                <Badge key={role} variant="outline">
                  {tRole(role as "admin")}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Info cards */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t("personalInfo")}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y">
                <DetailRow label={t("firstName")}>{firstNameStr || t("notAvailable")}</DetailRow>
                <DetailRow label={t("lastName")}>{lastNameStr || t("notAvailable")}</DetailRow>
                <DetailRow label={t("email")}>{email ?? t("notAvailable")}</DetailRow>
                <DetailRow label={t("fiscalNumber")}>
                  {fiscalNumber ?? t("notAvailable")}
                </DetailRow>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("accountInfo")}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y">
                <DetailRow label={t("status")}>
                  <Badge variant={getUserStatusVariant(user.status)}>
                    {tStatus(user.status as "active")}
                  </Badge>
                </DetailRow>
                <DetailRow label={t("roles")}>
                  {roles.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {roles.map((role) => (
                        <Badge key={role} variant="outline">
                          {tRole(role as "admin")}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    t("notAvailable")
                  )}
                </DetailRow>
                <DetailRow label={t("preferredLocale")}>
                  {user.preferred_locale
                    ? (localeLabels[user.preferred_locale] ?? user.preferred_locale)
                    : t("notAvailable")}
                </DetailRow>
                <DetailRow label={t("lastLogin")}>
                  {user.last_login
                    ? formatDate(user.last_login, locale)
                    : t("never")}
                </DetailRow>
                <DetailRow label={t("createdAt")}>
                  {formatDate(user.created_at, locale)}
                </DetailRow>
                <DetailRow label={t("updatedAt")}>
                  {formatDate(user.updated_at, locale)}
                </DetailRow>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
